import { useRef, useEffect, useCallback } from 'react'
import type { VisualState, VisualShape } from 'types'
import { DPR_CAP_DESKTOP, DPR_CAP_MOBILE } from '../../utils/visuals/config'
import { isHandheldViewport, type CanvasSurface } from '../../utils/visuals/surface-tune'
import { getLandingScrollProgress } from '../lib/landing-canvas-scroll.js'
import { LivingFieldEngine, type LivingFieldMode } from '../../utils/visuals/living-field-engine'
import {
  resolveLivingFieldCount,
  resolveLivingFieldMode,
} from '../../utils/visuals/living-field-mode'

export interface UseCanvasLoopProps {
  canvasRef: React.RefObject<HTMLCanvasElement>
  containerRef: React.RefObject<HTMLDivElement>
  visualState: VisualState
  isDarkMode: boolean
  surface?: CanvasSurface
  onShapeChange?: ((shape: VisualShape) => void) | undefined
  userProfile?: { name?: string; email?: string } | null
  getAudioData?: (() => { waveform?: Uint8Array; frequency?: Uint8Array } | null) | undefined
  /** Reduce field motion and ink while the composer or voice has focus. */
  calm?: boolean | undefined
  /** Skip VisualState→mode mapping (concierge preflight matches the lab organism). */
  livingModeOverride?: LivingFieldMode | undefined
}

function audioLevelFromWaveform(data: Uint8Array | undefined, fallback: number): number {
  if (!data || data.length === 0) return fallback
  let sum = 0
  for (let i = 0; i < data.length; i++) {
    const v = ((data[i] ?? 128) - 128) / 128
    sum += v * v
  }
  const rms = Math.sqrt(sum / data.length)
  // Follow live energy; never floor on a static VisualState.audioLevel (that looked manic).
  return Math.min(1, rms * 4.2)
}

/**
 * Canvas loop — living-field organism on chat / landing / page.
 * Legacy 47-shape Antigravity path remains in look-lab / canvas-lab only.
 */
export function useCanvasLoop(props: UseCanvasLoopProps) {
  const {
    canvasRef,
    containerRef,
    visualState,
    isDarkMode,
    surface = 'page',
    getAudioData,
    calm = false,
    livingModeOverride,
  } = props

  const engineRef = useRef<LivingFieldEngine | null>(null)
  const rafRef = useRef(0)
  const visibleRef = useRef(true)
  const stateRef = useRef({ visualState, isDarkMode, calm, livingModeOverride })
  stateRef.current = { visualState, isDarkMode, calm, livingModeOverride }
  const layoutRef = useRef({ surface })
  layoutRef.current = { surface }
  const reducedMotionRef = useRef(false)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => {
      reducedMotionRef.current = mq.matches
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const rect = container.getBoundingClientRect()
    const w = Math.max(1, Math.round(rect.width))
    const h = Math.max(1, Math.round(rect.height))
    const coarse =
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
    const handheld = isHandheldViewport(w, coarse)
    const dpr = Math.min(window.devicePixelRatio || 1, handheld ? DPR_CAP_MOBILE : DPR_CAP_DESKTOP)
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const count = resolveLivingFieldCount(
      stateRef.current.livingModeOverride ? 'landing' : layoutRef.current.surface,
      handheld,
      reducedMotionRef.current
    )
    if (!engineRef.current) engineRef.current = new LivingFieldEngine({ count })
    engineRef.current.resize(w, h, count)
  }, [canvasRef, containerRef])

  useEffect(() => {
    resize()
    const ro = new ResizeObserver(resize)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [resize, containerRef])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect || rect.width < 1 || rect.height < 1) return
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      if (x < -40 || y < -40 || x > rect.width + 40 || y > rect.height + 40) {
        pointerRef.current = null
        return
      }
      pointerRef.current = {
        x: (x / rect.width) * 2 - 1,
        y: (y / rect.height) * 2 - 1,
      }
    }
    const onLeave = () => {
      pointerRef.current = null
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerleave', onLeave, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerleave', onLeave)
    }
  }, [canvasRef])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    resize()
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let active = true
    if (!engineRef.current) {
      const coarse =
        typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
      const w = canvas.clientWidth || 1
      engineRef.current = new LivingFieldEngine({
        count: resolveLivingFieldCount(
          stateRef.current.livingModeOverride ? 'landing' : layoutRef.current.surface,
          isHandheldViewport(w, coarse),
          reducedMotionRef.current
        ),
      })
    }

    const io = new IntersectionObserver(
      entries => {
        visibleRef.current = entries[0]?.isIntersecting ?? true
      },
      { threshold: 0 }
    )
    io.observe(canvas)

    const loop = () => {
      if (!active) return
      // Chat + concierge override must keep painting even if IO misfires
      // (staging crop historically made the field look dead).
      const mustPaint = layoutRef.current.surface === 'chat'
      if (!visibleRef.current && !mustPaint) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      const engine = engineRef.current
      if (!engine) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      const st = stateRef.current
      const layout = layoutRef.current
      const now = performance.now()
      const audioData = getAudioData?.()
      let waveLevel = audioLevelFromWaveform(audioData?.waveform, st.visualState.audioLevel)
      if (st.livingModeOverride === 'speak') {
        // Fake sine for the ~2.2s chat-lock morph — high enough that speakWave
        // reads as grit layers, not a faint flat line. Range ~[0.32, 0.80].
        waveLevel = 0.56 + Math.sin(now * 0.0032) * 0.24
      } else if (st.livingModeOverride === 'search') {
        // Modest companion so staging search isn't dead; still well below speak.
        waveLevel = 0.18 + Math.sin(now * 0.002) * 0.08
      }
      const scrollProgress =
        st.livingModeOverride || layout.surface !== 'landing' ? 0 : getLandingScrollProgress()
      const livingMode =
        st.livingModeOverride ??
        resolveLivingFieldMode(st.visualState, {
          surface: layout.surface,
          scrollProgress,
        })

      // Reduced-motion users still get a stable field, but no continuous animation.
      engine.setPaused(reducedMotionRef.current)
      engine.setCalmMode(st.calm)
      engine.setMode(livingMode)
      engine.setDark(st.isDarkMode)
      engine.setAudioLevel(waveLevel)
      engine.setVoiceGain(st.livingModeOverride ? 1 : layout.surface === 'landing' ? 0.28 : 1)
      engine.setScrollProgress(scrollProgress)
      engine.setAnchorBias(st.visualState.anchorBias ?? (layout.surface === 'landing' ? 0.5 : 0.5))
      const ptr = pointerRef.current
      if (ptr) engine.setPointer(ptr.x, ptr.y)
      else engine.setPointer(null)

      engine.tick(now, ctx)
      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)
    return () => {
      active = false
      cancelAnimationFrame(rafRef.current)
      io.disconnect()
    }
  }, [canvasRef, isDarkMode, resize, getAudioData])

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect || rect.width < 1 || rect.height < 1) return
      pointerRef.current = {
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        y: ((e.clientY - rect.top) / rect.height) * 2 - 1,
      }
    },
    [canvasRef]
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      onPointerMove(e)
    },
    [onPointerMove]
  )

  const onPointerUp = useCallback(() => {
    /* living-field only needs continuous gaze */
  }, [])

  const onPointerLeave = useCallback(() => {
    pointerRef.current = null
  }, [])

  return { onPointerMove, onPointerDown, onPointerUp, onPointerLeave }
}
